import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, CheckCircle2, X, Upload } from "lucide-react";

export default function TradeIn() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    vin: "", year: "", make: "", model: "", trim: "",
    mileage: "", condition: "good", payoff: "0", notes: "",
  });
  const [photos, setPhotos] = useState([]); // [{path, url, name}]
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8 - photos.length);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("purpose", "trade_in_photo");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ path: data.path, url: data.url, name: f.name });
      } catch {
        toast.error(`Couldn't upload ${f.name}`);
      }
    }
    setPhotos((p) => [...p, ...uploaded]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", {
        type: "trade_in",
        payload: { ...form, photos: photos.map((p) => p.path), photo_count: photos.length },
      });
      setDone(true);
      toast.success("Trade-in submitted. Expect a valuation within 24 hours.");
    } catch {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[700px] mx-auto px-5 py-24 text-center" data-testid="tradein-success">
        <CheckCircle2 className="w-14 h-14 mx-auto mb-5 text-emerald-400" />
        <h2 className="font-display text-4xl font-light tracking-tighter mb-3">Valuation incoming.</h2>
        <p className="text-gray-400">We're pulling auction comps + KBB data. A broker will email a range within 24 hours.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="trade-in-page">
      <p className="label-mono mb-3">Trade-In Valuation</p>
      <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-5">Know what it's worth.</h1>
      <p className="text-gray-400 max-w-2xl mb-12">We pull auction comps, KBB, and wholesale data to give you a real trade range — often higher than the dealer's first offer.</p>

      <form onSubmit={submit} className="glass rounded-2xl p-7 md:p-10 grid md:grid-cols-2 gap-x-8 gap-y-5">
        <div className="md:col-span-2 label-mono">About You</div>
        <Input required placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="trade-name" />
        <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="trade-email" />
        <Input required placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white md:col-span-2" data-testid="trade-phone" />

        <div className="md:col-span-2 label-mono mt-2">Vehicle Details</div>
        <Input placeholder="VIN (17 chars, optional but speeds up)" value={form.vin} onChange={(e) => set("vin", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white md:col-span-2" data-testid="trade-vin" />
        <Input required placeholder="Year" value={form.year} onChange={(e) => set("year", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="trade-year" />
        <Input required placeholder="Make" value={form.make} onChange={(e) => set("make", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="trade-make" />
        <Input required placeholder="Model" value={form.model} onChange={(e) => set("model", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="trade-model" />
        <Input placeholder="Trim (optional)" value={form.trim} onChange={(e) => set("trim", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="trade-trim" />
        <Input required placeholder="Mileage" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white" data-testid="trade-mileage" />
        <select value={form.condition} onChange={(e) => set("condition", e.target.value)} className="bg-[#0A0A0E] border border-[#1A1A24] h-12 rounded-xl text-white px-3 text-sm" data-testid="trade-condition">
          <option value="excellent">Excellent</option>
          <option value="good">Good</option>
          <option value="fair">Fair</option>
          <option value="rough">Rough</option>
        </select>
        <Input placeholder="Loan payoff balance ($, 0 if none)" value={form.payoff} onChange={(e) => set("payoff", e.target.value)} className="bg-[#0A0A0E] border-[#1A1A24] h-12 rounded-xl text-white md:col-span-2" data-testid="trade-payoff" />

        <textarea
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Any damage, recent service, accidents, modifications?"
          rows={3}
          className="md:col-span-2 w-full bg-[#0A0A0E] border border-[#1A1A24] rounded-xl text-white p-3 text-sm"
          data-testid="trade-notes"
        />

        <div className="md:col-span-2">
          <div className="label-mono mb-3 flex items-center gap-2"><Camera className="w-3 h-3" /> Vehicle Photos (up to 8)</div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickFiles} data-testid="trade-photo-input" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.path} className="relative aspect-square rounded-xl overflow-hidden border border-[#1A1A24]" data-testid={`trade-photo-${p.path}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(p.path)} className="absolute top-1 right-1 w-7 h-7 rounded-full glass-strong flex items-center justify-center" aria-label="Remove">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {photos.length < 8 && (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="aspect-square rounded-xl border border-dashed border-white/15 hover:border-white/30 flex flex-col items-center justify-center gap-1 hover:bg-white/[0.02] disabled:opacity-50" data-testid="trade-photo-upload-btn">
                <Upload className="w-4 h-4 text-silver" />
                <span className="text-[10px] text-gray-400">{uploading ? "Uploading…" : "Add photos"}</span>
              </button>
            )}
          </div>
          <p className="text-[10px] text-gray-500 mt-2">Photos boost valuation accuracy by 20%+. Exterior, interior, dash, and any damage.</p>
        </div>

        <div className="md:col-span-2">
          <button type="submit" disabled={busy} className="w-full md:w-auto px-10 py-4 rounded-full silver-gradient text-[#050507] font-bold tracking-wide disabled:opacity-50" data-testid="trade-submit">
            {busy ? "Sending…" : "Get My Valuation"}
          </button>
        </div>
      </form>
    </div>
  );
}
